import type { StyleObjectOrFn } from "@chakra-ui/styled-system";

const defaultTheme: StyleObjectOrFn = {
  colors: {
    primary: {
      "50": "#FCFCFF",
      "100": "#D6E4FF",
      "200": "#ADC8FF",
      "300": "#84A9FF",
      "400": "#6690FF",
      "500": "#3366FF",
      "600": "#254EDB",
      "700": "#1939B7",
      "800": "#102693",
      "900": "#091A7A",
    },
    success: {
      "100": "#EFFDD4",
      "200": "#DBFBAB",
      "300": "#BEF37F",
      "400": "#A1E75E",
      "500": "#77D82D",
      "600": "#5AB920",
      "700": "#419B16",
      "800": "#2B7D0E",
      "900": "#1C6708",
    },
    info: {
      "100": "#D8F7FE",
      "200": "#B1EBFE",
      "300": "#8BDAFD",
      "400": "#6DC8FB",
      "500": "#3EABF9",
      "600": "#2D86D6",
      "700": "#1F64B3",
      "800": "#134690",
      "900": "#0B3177",
    },
    warning: {
      "100": "#FFF6D9",
      "200": "#FFEBB4",
      "300": "#FFDE8E",
      "400": "#FFD072",
      "500": "#FFBA44",
      "600": "#DB9631",
      "700": "#B77522",
      "800": "#935715",
      "900": "#7A410D",
    },
    danger: {
      "100": "#FFE7D5",
      "200": "#FFC8AC",
      "300": "#FFA382",
      "400": "#FF8063",
      "500": "#FF4530",
      "600": "#DB2623",
      "700": "#B71821",
      "800": "#930F22",
      "900": "#7A0922",
    },
    surface: {
      white: "#FFFFFF",
      "05": "#F4F4F4",
      10: "#EAEAEA",
      20: "#D3D3D3",
      30: "#C1C1C1",
      40: "#ADADAD",
      50: "#989898",
      100: "#848484",
      200: "#6F6F6F",
      300: "#5B5B5B",
      400: "#474747",
      500: "#333333",
      600: "#1F1F1F",
      700: "#141414",
      800: "#0A0A0A",
      900: "#030303",
    },
    grayList: {
      "50": "#F3f3f3",
      "100": "#F4F4F4",
      "200": "#D3D3D3",
      "300": "#BDBDBD",
      "400": "#A8A8A8",
      "500": "#949494",
      "600": "#7F7F7F",
      "700": "#6B6B6B",
      "800": "#575757",
      "900": "#434343",
    },
    white: "#FFFFFF",
    background: {
      nav: "#ADC8FF",
    },
    seatColors: {
      available: "#9F9E9F",
      socialDistancing: "blackAlpha.500",
      selected: "#55C746",
      booked: "#FFB833",
      sold: "#B72136",
      premium: "#935810",
      hold: "#ADC8FF",
    },
  },
  fonts: {
    body: "'DM Sans', sans-serif",
    heading: "DM Sans, sans-serif",
  },
  shadows: {
    csm: "0px 1px 4px rgba(0, 0, 0, 0.1)",
  },
  components: {
    Toast: {
      baseStyle: {
        zIndex: "9999",
      },
    },
    FormLabel: {
      baseStyle: {
        fontSize: "12px",
        fontWeight: 600,
        color: "#3A4150",
        marginBottom: "7px",
        lineHeight: 1.3,
      },
    },
    FormHelperText: {
      baseStyle: {
        fontSize: "11.5px",
        color: "#9AA2AE",
        marginTop: "6px",
        lineHeight: 1.4,
      },
    },
    FormError: {
      baseStyle: {
        text: {
          fontSize: "11.5px",
          color: "#DC2626",
          marginTop: "5px",
          fontWeight: 500,
          lineHeight: 1.4,
        },
      },
    },
    Checkbox: {
      baseStyle: {
        control: {
          w: "16px",
          h: "16px",
          borderRadius: "4px",
          border: "1.5px solid",
          borderColor: "#C7CCD4",
          bg: "white",
          _checked: {
            bg: "#3A4BD8",
            borderColor: "#3A4BD8",
            color: "white",
            _hover: { bg: "#3140C2", borderColor: "#3140C2" },
          },
          _hover: { borderColor: "#9AA2AE" },
          _focusVisible: {
            boxShadow: "0 0 0 3px rgba(58,75,216,0.2)",
          },
          _invalid: {
            borderColor: "#DC2626",
          },
          _disabled: {
            bg: "#F6F7F9",
            borderColor: "#E5E8EC",
            _checked: { bg: "#C7CCD4", borderColor: "#C7CCD4" },
          },
        },
        label: {
          fontSize: "13px",
          fontWeight: 500,
          color: "#14181F",
          marginLeft: "8px",
        },
      },
    },
    Table: {
      variants: {
        sageStriped: {
          tbody: {
            tr: {
              _odd: {
                bg: "info.100",
                _hover: {
                  bg: "info.200",
                },
              },
              _even: {
                bg: "grayList.100",
                _hover: {
                  bg: "grayList.200",
                },
              },
            },
          },
        },
        admin: {
          thead: {
            bg: "#FCFCFF",
            th: {
              fontFamily: "body",
              fontSize: "14px",
              color: "#474747",
              padding: "1.7rem",
              "border-bottom": "1px solid #F3f3f3",
            },
            w: "100%",
          },
          tbody: {
            tr: {
              "border-bottom": "1px solid #F3f3f3",
              fontSize: "14px",
              _hover: {
                bg: "gray.100",
              },
            },
          },
          tfoot: {
            bg: "#FCFCFF",
            th: {
              fontFamily: "body",
              fontSize: "14px",
              color: "#474747",
              padding: "1.7rem",
              "border-bottom": "1px solid #F3f3f3",
            },
            w: "100%",
          },
        },
      },
    },
    Text: {
      baseStyle: {
        fontFamily: "body",
        fontWeight: "normal",
      },
      sizes: {
        xs: {
          fontSize: "0.75rem",
        },
        sm: {
          fontSize: "0.8rem",
        },
        base: {
          fontSize: "1rem",
        },
        lg: {
          fontSize: "1.25rem",
        },
        xl: {
          fontSize: "1.5rem",
        },
        "2xl": {
          fontSize: "1.875rem",
        },
      },
      variants: {
        heading: {
          fontSize: "2em",
          lineHeight: "1.25em",
          fontWeight: 600,
        },
        subtitle: {
          fontSize: "1em",
          lineHeight: "1.875em",
          fontWeight: 500,
          color: "gray.600",
        },
        caption: {
          fontSize: "0.875em",
          lineHeight: "1.25em",
          fontWeight: 400,
          color: "gray.600",
        },
        link: {
          fontSize: "1em",
          lineHeight: "1.875em",
          fontWeight: 600,
          color: "primary.600",
          cursor: "pointer",
          _hover: {
            textDecoration: "underline",
            color: "primary.600",
          },
        },
      },
    },

    Container: {
      baseStyle: {
        maxW: {
          "2xl": "100em",
          xl: "container.xl",
          sm: "container.sm",
          md: "container.md",
          lg: "container.lg",
        },
      },
    },
    Button: {
      baseStyle: {
        py: "8px",
        px: "16px",
        borderRadius: "16px",
        fontSize: "0.875rem",
        fontWeight: "700",
        fontFamily: "body",
        lineHeight: "18.23px",
      },

      defaultProps: {
        colorScheme: "primary",
      },
      variants: {
        add: {
          bg: "white",
          color: "blue.500",
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.2)",
          _hover: {
            bg: "blue.500",
            color: "white",
            _disabled: {
              bg: "white",
              color: "blue.500",
            },
          },
        },
        delete: {
          bg: "white",
          color: "red.500",
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.2)",
          _hover: {
            bg: "red.500",
            color: "white",
            _disabled: {
              bg: "white",
              color: "red.500",
            },
          },
        },
      },
    },
  },
};

export default defaultTheme;
